import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Mail, Clock, Calendar, Bell, Check, Settings, Loader2, ChevronRight, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

import { API } from '../config/api';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_SLOTS = ['06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'];

export default function WeeklyEmailSettings({ childId, childName, parentEmail }) {
  const [settings, setSettings] = useState({
    enabled: false,
    frequency: 'weekly',
    day_of_week: 'Monday',
    time: '09:00',
    include_growth: true,
    include_performance: true,
    include_nutrition: true,
    include_goals: true,
    include_ai_insights: true,
    emails: parentEmail ? [parentEmail] : []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testSending, setTestSending] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/email-reminders/settings/${childId}`);
      if (res.data.settings) {
        // Convert old single email format to array if needed
        const loadedSettings = { ...res.data.settings };
        if (loadedSettings.email && !loadedSettings.emails) {
          loadedSettings.emails = [loadedSettings.email];
        }
        if (!loadedSettings.emails) {
          loadedSettings.emails = [];
        }
        setSettings(prev => ({ ...prev, ...loadedSettings }));
      }
    } catch (err) {
      // No existing settings - use defaults
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const addEmail = () => {
    if (!newEmail) return;
    if (!newEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (settings.emails.includes(newEmail)) {
      toast.error('This email is already added');
      return;
    }
    if (settings.emails.length >= 5) {
      toast.error('Maximum 5 email addresses allowed');
      return;
    }
    setSettings(prev => ({ ...prev, emails: [...prev.emails, newEmail] }));
    setNewEmail('');
  };

  const removeEmail = (emailToRemove) => {
    setSettings(prev => ({ 
      ...prev, 
      emails: prev.emails.filter(e => e !== emailToRemove) 
    }));
  };

  const saveSettings = async () => {
    if (settings.enabled && settings.emails.length === 0) {
      toast.error('Please add at least one email address');
      return;
    }

    setSaving(true);
    try {
      // Send both formats for backward compatibility
      await axios.post(`${API}/email-reminders/settings/${childId}`, {
        ...settings,
        email: settings.emails[0] || '' // Keep primary email for compatibility
      });
      toast.success('Email reminder settings saved!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (settings.emails.length === 0) {
      toast.error('Please add at least one email address');
      return;
    }

    setTestSending(true);
    try {
      await axios.post(`${API}/email-reminders/test/${childId}`, {
        emails: settings.emails
      });
      toast.success(`Test email sent to ${settings.emails.length} recipient(s)!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestSending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 text-center">
        <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="weekly-email-settings">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Weekly Progress Emails</h3>
              <p className="text-xs text-zinc-400">Automated reports for {childName}</p>
            </div>
          </div>
          
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
          />
        </div>
      </div>

      <div className="p-4 space-y-6">
        {/* Email Addresses - Multiple */}
        <div>
          <label className="block text-sm font-medium text-zinc-300 mb-2">
            Email Recipients
            <span className="text-xs text-zinc-500 ml-2">(up to 5)</span>
          </label>
          
          {/* Current emails */}
          <div className="space-y-2 mb-3">
            {settings.emails.map((email, idx) => (
              <div 
                key={email}
                className="flex items-center gap-2 p-2 bg-zinc-800 rounded-lg border border-zinc-700"
              >
                <Mail className="h-4 w-4 text-zinc-500" />
                <span className="flex-1 text-sm text-white">{email}</span>
                {idx === 0 && (
                  <span className="text-xs text-[#02b5ff] px-2 py-0.5 bg-[#02b5ff]/10 rounded">Primary</span>
                )}
                <button
                  onClick={() => removeEmail(email)}
                  className="p-1 hover:bg-zinc-700 rounded text-zinc-500 hover:text-red-400"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            {settings.emails.length === 0 && (
              <p className="text-sm text-zinc-500 italic">No email addresses added yet</p>
            )}
          </div>
          
          {/* Add new email */}
          {settings.emails.length < 5 && (
            <div className="flex gap-2">
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                placeholder="Add another email address"
                className="bg-zinc-800 border-zinc-700 text-white flex-1"
                data-testid="add-email-input"
              />
              <Button
                onClick={addEmail}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          <p className="text-xs text-zinc-500 mt-2">
            All listed recipients will receive the weekly progress report
          </p>
        </div>

        {/* Schedule */}
        <div className={settings.enabled ? '' : 'opacity-50 pointer-events-none'}>
          <label className="block text-sm font-medium text-zinc-300 mb-3">
            <Calendar className="h-4 w-4 inline mr-2" />
            Delivery Schedule
          </label>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Day Selection */}
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Day of Week</label>
              <select
                value={settings.day_of_week}
                onChange={(e) => setSettings(prev => ({ ...prev, day_of_week: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
              >
                {DAYS_OF_WEEK.map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
            
            {/* Time Selection */}
            <div>
              <label className="block text-xs text-zinc-500 mb-1">Time</label>
              <select
                value={settings.time}
                onChange={(e) => setSettings(prev => ({ ...prev, time: e.target.value }))}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white"
              >
                {TIME_SLOTS.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          </div>
          
          <p className="text-xs text-zinc-500 mt-2">
            <Clock className="h-3 w-3 inline mr-1" />
            Reports will be sent every {settings.day_of_week} at {settings.time} UTC
          </p>
        </div>

        {/* Report Contents */}
        <div className={settings.enabled ? '' : 'opacity-50 pointer-events-none'}>
          <label className="block text-sm font-medium text-zinc-300 mb-3">
            <Settings className="h-4 w-4 inline mr-2" />
            Report Contents
          </label>
          
          <div className="space-y-3">
            {[
              { key: 'include_growth', label: 'Growth & Measurements', desc: 'Height, weight, BMI changes' },
              { key: 'include_performance', label: 'Athletic Performance', desc: 'Test results and improvements' },
              { key: 'include_nutrition', label: 'Nutrition Summary', desc: 'Meals logged and calorie tracking' },
              { key: 'include_goals', label: 'Goals & Targets', desc: 'Progress on set targets' },
              { key: 'include_ai_insights', label: 'AI Coach Insights', desc: 'Personalized recommendations' }
            ].map(item => (
              <div 
                key={item.key}
                className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg border border-zinc-700"
              >
                <div>
                  <div className="text-sm font-medium text-white">{item.label}</div>
                  <div className="text-xs text-zinc-500">{item.desc}</div>
                </div>
                <Switch
                  checked={settings[item.key]}
                  onCheckedChange={(checked) => setSettings(prev => ({ ...prev, [item.key]: checked }))}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-zinc-800">
          <Button
            onClick={saveSettings}
            disabled={saving}
            className="flex-1 bg-[#02b5ff] hover:bg-[#02b5ff]/80"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Save Settings
          </Button>
          
          <Button
            variant="outline"
            onClick={sendTestEmail}
            disabled={testSending || !settings.email}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            {testSending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
            Send Test
          </Button>
        </div>

        {/* Preview Info */}
        {settings.enabled && settings.emails.length > 0 && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Bell className="h-4 w-4 text-green-400 mt-0.5" />
              <div>
                <p className="text-sm text-green-400 font-medium">Weekly reports enabled</p>
                <p className="text-xs text-green-400/70">
                  Next email: {settings.day_of_week} at {settings.time} to {settings.emails.length} recipient{settings.emails.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
